import { useCallback, useEffect, useRef } from "react";

import { usePathname } from "next/navigation";

import { useConfig } from "@jti/app-context/useConfig";
import { getCookie, setCookie, setProvinceCookies } from "@jti/fe-common";
import { calculateExpireDate, clearUser, getUser } from "@jti/rest-api";
import { checkLoggedIn } from "@jti/ui/components/ContentGate";

const USER_LOGGED_IN_COOKIE_NAME = "isUserLoggedIn";

/**
 * Reconciles the browser login marker with the stored CIAM/CAS session.
 * It intentionally fails closed so the edge can re-evaluate hard-gate rules
 * after stale client state is removed.
 * Tickets: DESP-124680, DESP-155518
 */
export const useUserSessionCleanup = () => {
  const pathname = usePathname();
  const config = useConfig();
  const isValidationPending = useRef(false);
  const isReloadScheduled = useRef(false);

  const reloadOnce = useCallback((beforeReload: () => boolean) => {
    if (isReloadScheduled.current) {
      return;
    }

    const shouldReload = beforeReload();

    if (!shouldReload) {
      return;
    }

    isReloadScheduled.current = true;
    window.location.reload();
  }, []);

  const clearSessionAndReload = useCallback(() => {
    reloadOnce(() => {
      clearUser();
      const cookieAfterClear = getCookie(USER_LOGGED_IN_COOKIE_NAME);
      return cookieAfterClear === undefined;
    });
  }, [reloadOnce]);

  const reconcileUserSession = useCallback(async () => {
    if (isValidationPending.current || isReloadScheduled.current) {
      return;
    }

    try {
      const userInStorage = getUser();
      const isUserLoggedInCookie = getCookie(USER_LOGGED_IN_COOKIE_NAME);
      const hasLoginCookie = isUserLoggedInCookie !== undefined;
      const isCookieTruthy = isUserLoggedInCookie === true || isUserLoggedInCookie === "true";
      const ciamToken = userInStorage.ciam_token;

      // Cookie is set but user data is missing in browser storage.
      if (hasLoginCookie && !ciamToken) {
        clearSessionAndReload();
        return;
      }

      if (!ciamToken) {
        return;
      }

      isValidationPending.current = true;

      const isSessionValid = await checkLoggedIn(userInStorage, config).catch((error: unknown) => {
        console.error("User session reconciliation error:", error);
        return false;
      });

      // Ignore a response for a user that changed while validation was pending.
      if (getUser().ciam_token !== ciamToken) {
        return;
      }

      if (!isSessionValid) {
        clearSessionAndReload();
        return;
      }

      // Cookie is missing or has an invalid value, but the stored token is valid.
      if (!isCookieTruthy) {
        reloadOnce(() => {
          setProvinceCookies(userInStorage.province_name);
          setCookie(USER_LOGGED_IN_COOKIE_NAME, true, "/", calculateExpireDate(7).toUTCString());
          const loginCookie = getCookie(USER_LOGGED_IN_COOKIE_NAME);
          return loginCookie === true || loginCookie === "true";
        });
      }
    } catch (error) {
      console.error("User session reconciliation error:", error);
      clearSessionAndReload();
    } finally {
      isValidationPending.current = false;
    }
  }, [clearSessionAndReload, config, reloadOnce]);

  useEffect(() => {
    void reconcileUserSession();
  }, [pathname, reconcileUserSession]);

  useEffect(() => {
    const handleFocus = () => {
      void reconcileUserSession();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void reconcileUserSession();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [reconcileUserSession]);
};
